import React, {useState, useEffect, useRef, useMemo, useCallback} from "react";
import {useDispatch, useSelector} from "react-redux";

//helpers:
import {useTranslation} from "react-i18next";
import {sendDataLayer} from "../../../util/tagmanager";

//actions:
import {trackPageActions} from "../../../redux/TrackPage/trackPageRedux";

//components:
import {NetgraphWrapper} from "../../../widgets";

//material:
import {Fade} from "@material-ui/core";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";

const TrackPageNetgraph = ({
    trackId,
    trackName,
    setSelectedNode,
    selectedInsights,
    selectedNode,
    filter1Selected,
    selectedVideos,
    selectedSpeakers,
    selectedTopics,
}) => {
    const {t} = useTranslation();
    const containerRef = useRef();

    const selectedInsightsKeys = useMemo(() => {
        const filter1ID = filter1Selected?.[0]?.id;
        const badgeKey = selectedInsights?.[0]?.key || "";
        if (filter1ID) {
            if (filter1ID === "highlights") return ["highlights"];
            else if (filter1ID === "topics") return ["category1"];
            return [badgeKey];
        }
        return [""];
    }, [filter1Selected, selectedInsights]);

    //local state:
    const [netgraphRatio, setNetgraphRatio] = useState(16 / 9);
    const [containerHeight, setContainerHeight] = useState(0);

    //refs:
    const selectedNodeRef = useRef();
    selectedNodeRef.current = selectedNode;

    //redux:
    //actions:
    const dispatch = useDispatch();
    const getDetails = ({trackId, filter2IDs, feature_id, speaker_id, video_id, topic_id}) =>
        dispatch(trackPageActions.getDetails({trackId, filter2IDs, feature_id, speaker_id, video_id, topic_id}));
    const clearDetails = () => dispatch(trackPageActions.clearDetails());
    //store:
    const contextSelector = useSelector(state => state.context);
    const netGraphData = useSelector(state => state.trackPage.netGraphData);
    const netgraphTrackError = useSelector(state => state.trackPage.netgraphTrackError);
    const netgraphConfig = useSelector(state => state.trackPage.netgraphConfig);
    const isLoadingNetgraphData = useSelector(state => state.trackPage.isLoadingNetgraphData);
    const isLoadingNetgraphConfig = useSelector(state => state.trackPage.isLoadingNetgraphConfig);

    const tagmanagerData = {
        track_id: trackId,
        track_name: trackName,
    };

    const updateNetgraphHeight = useCallback(() => {
        const screenWidth = window.innerWidth;
        const isDesktop = screenWidth > 768;
        const newRatio = isDesktop ? 16 / 9 : 16 / 14;

        setNetgraphRatio(newRatio);

        const width = containerRef?.current?.offsetWidth;
        const newHeight = width ? width / newRatio : 0;

        setContainerHeight(newHeight);
    }, [containerRef, setContainerHeight]);

    useEffect(() => {
        updateNetgraphHeight();
        window.addEventListener("resize", updateNetgraphHeight);

        return () => window.removeEventListener("resize", updateNetgraphHeight);
    }, []);

    const onNetgraphNodeSelection = nodeObj => {
        const isNotTrack = nodeObj?.node && nodeObj?.node !== "track";
        if (nodeObj && nodeObj.name && isNotTrack) {
            setSelectedNode(null);

            let speaker_id = null;
            let video_id = null;
            let topic_id = null;
            let feature_id = nodeObj.id || null;
            let filter2IDs = null;

            const selectedFilter1ID = filter1Selected?.[0]?.id;

            if (selectedFilter1ID === "speakers") {
                speaker_id = nodeObj.id;
                if (selectedSpeakers && selectedSpeakers[0] && selectedSpeakers[0].id) {
                    filter2IDs = selectedSpeakers.map(speaker => speaker.id);
                }
            } else if (selectedFilter1ID === "videos") {
                video_id = nodeObj.id;
                if (selectedVideos && selectedVideos[0] && selectedVideos[0].id) {
                    filter2IDs = selectedVideos.map(video => video.id);
                }
            } else if (selectedFilter1ID === "topics") {
                topic_id = nodeObj.id;
                if (selectedTopics && selectedTopics[0] && selectedTopics[0].id) {
                    filter2IDs = selectedTopics.map(topic => topic.id);
                }
            }

            getDetails({trackId, filter2IDs, feature_id, speaker_id, video_id, topic_id});
            setSelectedNode(nodeObj);

            sendDataLayer(
                {
                    ...tagmanagerData,
                    event: "track_netgraph_select",
                    selectionNode: nodeObj.name,
                    selection_type: filter1Selected?.[0]?.id,
                    selection_ids: filter2IDs,
                    selection_insight: selectedInsights?.[0]?.key,
                },
                {context: contextSelector}
            );
        }
    };

    const onClearNodeSelection = () => {
        clearDetails();
        setSelectedNode(null);
    };

    const unselectNode = () => {
        if (selectedNodeRef.current) {
            sendDataLayer(
                {...tagmanagerData, event: "track_netgraph_reset", click_reset: false},
                {context: contextSelector}
            );
            onClearNodeSelection();
        }
    };

    const onClickReset = () => {
        if (selectedNode) {
            sendDataLayer(
                {...tagmanagerData, event: "track_netgraph_reset", click_reset: true},
                {context: contextSelector}
            );
            onClearNodeSelection();
        }
    };

    const content = () => {
        if (netgraphTrackError && netgraphTrackError.text)
            return (
                <div className="h-100 w-100 d-flex justify-content-center align-items-center position-absolute z-index-2">
                    <div className={`alert alert-${netgraphTrackError.type || "info"} font-size-h5 font-weight-bold`}>
                        <WarningRoundedIcon color="inherit" className="mr-5" />
                        {netgraphTrackError.text}
                    </div>
                </div>
            );
        if (netGraphData)
            return (
                <div className="track_page_netgraph_container">
                    <NetgraphWrapper
                        aspectRatio={netgraphRatio}
                        config={netgraphConfig}
                        isLoading={isLoadingNetgraphData || isLoadingNetgraphConfig}
                        data={netGraphData}
                        features={selectedInsightsKeys}
                        nodeSelectionCallback={onNetgraphNodeSelection}
                        nodeUnselectionCallback={unselectNode}
                        selectedNodeName={selectedNode?.name || null}
                    />
                    <Fade in={selectedNode?.name}>
                        <button
                            className="trackpage_resetButton btn btn-light btn-shadow btn-lg font-weight-bold font-size-h5"
                            onClick={onClickReset}
                        >
                            {t("NETGRAPH.RESET")}
                        </button>
                    </Fade>
                </div>
            );
        return <div />;
    };

    return (
        <div
            className="card category-network-chart-container rounded gutter-b border"
            ref={containerRef}
            style={{height: containerHeight}}
        >
            {content()}
        </div>
    );
};

export default TrackPageNetgraph;
